import { NetworkStatus, useQuery } from "@apollo/client";
import {
  AssetCard,
  Button,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  PrimaryFullScreenSpinner,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { Suspense } from "react";
import { RiAddLine } from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { depositsPageOrderValues } from "~/gql/client/enumValues";
import type {
  DepositRowFragment,
  DepositsPageOrder,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/deposits")({
  head: () => opsHead("Deposits"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const PAGE_SIZE = 50;

function View() {
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  const [sortOrder, setSortOrder] = useQueryPersistedStateWithSchema({
    useSearchParams,
    schema: z.object({
      sortOrder: z.enum(depositsPageOrderValues).optional().nullable(),
    }),
    defaultState: {
      sortOrder: "DATE_DESC",
    },
  });
  const { sortOrder: order } = sortOrder;

  const { data, loading, networkStatus, fetchMore } = useQuery(
    graphql(`
      query AdminDeposits(
        $after: String
        $first: Int!
        $order: DepositsPageOrder!
      ) {
        deposits(after: $after, first: $first, order: $order) {
          pageInfo {
            endCursor
            hasNextPage
          }
          deposits {
            ...DepositRow
          }
        }
      }

      fragment DepositRow on Deposit {
        id
        date
        amount {
          amount
          currency
        }
        destinationAsset {
          ...AssetCardFields
        }
      }
    `),
    {
      variables: {
        after: null,
        first: PAGE_SIZE,
        order: order ?? "DATE_DESC",
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const deposits = data?.deposits?.deposits ?? [];
  const pageInfo = data?.deposits?.pageInfo;
  const hasNextPage = !!pageInfo?.hasNextPage;
  const endCursor = pageInfo?.endCursor ?? null;

  const loadingMore = networkStatus === NetworkStatus.fetchMore;
  const initialLoading =
    loading && networkStatus === NetworkStatus.loading && deposits.length === 0;

  const handleLoadMore = () => {
    if (!hasNextPage || !endCursor) {
      return;
    }
    void fetchMore({
      variables: {
        after: endCursor,
        first: PAGE_SIZE,
        order,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.deposits) {
          return prev;
        }
        if (!prev.deposits) {
          return fetchMoreResult;
        }
        return {
          ...prev,
          deposits: {
            ...fetchMoreResult.deposits,
            deposits: [
              ...prev.deposits.deposits,
              ...fetchMoreResult.deposits.deposits,
            ],
          },
        };
      },
    });
  };

  const handleSortChange = (newSortingState: SortingState) => {
    const primarySort = newSortingState.at(0);
    let newSortOrder: DepositsPageOrder;
    if (primarySort?.id === "date") {
      newSortOrder = primarySort.desc ? "DATE_DESC" : "DATE_ASC";
    } else {
      newSortOrder = "DATE_DESC";
    }
    if (newSortOrder !== order) {
      setSortOrder({ sortOrder: newSortOrder });
    }
  };

  return (
    <AdminPageContainer testId="admin-deposits">
      <AdminPageHeader
        title="Deposits"
        subtitle="Deposits to Primary-managed assets."
        buttons={
          <div className="flex gap-2">
            <Link to="/deposits/new">
              <Button size="sm">
                <RiAddLine className="mr-1" />
                New deposit
              </Button>
            </Link>
          </div>
        }
      />
      <div className="pt-2">
        {deposits.length === 0 ? (
          <div className="border border-zinc-200 border-dashed p-8 text-center text-neutral-500 text-sm">
            {initialLoading ? "Loading deposits..." : "No deposits"}
          </div>
        ) : (
          <DataTable
            data={deposits}
            columns={columns}
            onRowClick={(row) => {
              navigate({
                to: "/deposits/$depositId",
                params: { depositId: row.original.id },
              });
            }}
            columnSortProps={{
              id: "date",
              desc: true,
            }}
            onSortingChange={handleSortChange}
            isLoadingData={initialLoading}
            manualSorting={true}
            pageSize={10_000}
            manualPagination={true}
            className="overflow-hidden border border-zinc-200"
          />
        )}
        {hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading..." : "Load more"}
            </Button>
          </div>
        )}
      </div>
    </AdminPageContainer>
  );
}

const columns: ColumnDef<DepositRowFragment>[] = [
  {
    id: "date",
    accessorKey: "date",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const date = new Date(row.original.date);
      return <DateUI date={date} />;
    },
    sortingFn: (a, b) => compareAsc(a.original.date, b.original.date),
  },
  {
    id: "amount",
    accessorKey: "amount",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" />
    ),
    cell: ({ row }) => {
      const amount = row.original.amount;
      if (!amount) {
        return <span>-</span>;
      }
      return (
        <CurrencyAmountUI currency={amount.currency} amount={amount.amount} />
      );
    },
    sortingFn: (a, b) => {
      const aAmount = a.original.amount?.amount;
      const bAmount = b.original.amount?.amount;
      if (!aAmount || !bAmount) return 0;
      return aAmount.comparedTo(bAmount);
    },
  },
  {
    id: "destinationAsset",
    accessorKey: "destinationAsset",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Destination Asset" />
    ),
    cell: ({ row }) =>
      row.original.destinationAsset ? (
        <AssetCard
          variant="BUSINESS_ENTITY"
          asset={row.original.destinationAsset}
        />
      ) : (
        <span>-</span>
      ),
    sortingFn: (a, b) =>
      (a.original.destinationAsset?.id ?? "").localeCompare(
        b.original.destinationAsset?.id ?? "",
      ),
  },
];

function compareAsc(a: Date, b: Date) {
  if (a.getTime() === b.getTime()) return 0;
  return a.getTime() < b.getTime() ? -1 : 1;
}
